import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { adminUpload } from "@/lib/adminUpload";
import { Loader2, Plus, Trash2, Upload, Save, Mail, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";

const BLANK_HALL = { name: "", capacity: 0, price: "", features: [], image_url: "", order: 0 };
const BLANK_ADDON = { name: "", capacity: 0, price: "", features: [], image_url: "", order: 0 };
const BLANK_STEP = { title: "", description: "", order: 0 };

export default function FacilityRentalsAdmin() {
  const [data, setData] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("content");

  useEffect(() => {
    (async () => {
      try {
        const [d, i] = await Promise.all([
          api.get("/admin/facility-rentals"),
          api.get("/admin/facility-inquiries"),
        ]);
        setData(d.data); setInquiries(i.data);
      } finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/facility-rentals", data);
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const uploadImage = adminUpload;

  const deleteInquiry = async (id) => {
    if (!window.confirm("Delete inquiry?")) return;
    try { await api.delete(`/admin/facility-inquiries/${id}`); setInquiries((p) => p.filter((i) => i.id !== id)); toast.success("Deleted"); }
    catch { toast.error("Failed"); }
  };

  if (loading) return <div className="text-ink/40">Loading…</div>;
  if (!data) return <div className="text-ink/40">No data</div>;

  return (
    <div data-testid="admin-facility-rentals-page">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Spaces</div>
          <h1 className="font-serif text-4xl mt-2 text-ink">Facility Rentals</h1>
          <p className="mt-2 text-ink/60 text-sm max-w-2xl">Edit everything shown on the public <a href="/facility-rentals" target="_blank" rel="noreferrer" className="text-navy underline">Facility Rentals page</a> — hero, halls, prices, process, rules, gallery, inquiry form.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="fr-save" className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-6 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-navy-900 disabled:opacity-60">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save changes
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-sand mb-8 overflow-x-auto admin-scroll">
        {["content", "halls", "addons", "process", "gallery", "inquiries"].map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`shrink-0 px-4 py-3 text-sm font-semibold uppercase tracking-wider transition-colors ${tab === t ? "text-navy border-b-2 border-gold" : "text-ink/50 hover:text-navy"}`}
            data-testid={`fr-tab-${t}`}>
            {t === "inquiries" ? `Inquiries (${inquiries.length})` : t === "addons" ? "Add-ons" : t}
          </button>
        ))}
      </div>

      {tab === "content" && (
        <div className="space-y-5 max-w-3xl">
          <Text label="Hero eyebrow" value={data.hero_eyebrow} onChange={(v) => setData({ ...data, hero_eyebrow: v })} testId="fr-hero-eyebrow" />
          <Text label="Hero title" value={data.hero_title} onChange={(v) => setData({ ...data, hero_title: v })} testId="fr-hero-title" />
          <Text label="Hero subtitle" value={data.hero_subtitle} onChange={(v) => setData({ ...data, hero_subtitle: v })} testId="fr-hero-subtitle" />
          <ImageField label="Hero image" value={data.hero_image} onChange={(v) => setData({ ...data, hero_image: v })} upload={uploadImage} testId="fr-hero-image" />
          <TextArea label="Intro (markdown)" rows={6} value={data.intro_markdown} onChange={(v) => setData({ ...data, intro_markdown: v })} testId="fr-intro" />
          <TextArea label="Rules / fine print (markdown)" rows={8} value={data.rules_markdown} onChange={(v) => setData({ ...data, rules_markdown: v })} testId="fr-rules" />
          <div className="grid md:grid-cols-2 gap-4">
            <Text label="Inquiry notification email (To)" value={data.inquiry_to_email} onChange={(v) => setData({ ...data, inquiry_to_email: v })} testId="fr-inq-email" />
            <Text label="Inquiry CC email" value={data.inquiry_cc_email} onChange={(v) => setData({ ...data, inquiry_cc_email: v })} testId="fr-inq-cc-email" />
          </div>
          <label className="flex items-center gap-3"><input type="checkbox" checked={!!data.inquiry_enabled} onChange={(e) => setData({ ...data, inquiry_enabled: e.target.checked })} data-testid="fr-inq-enabled" className="w-5 h-5 accent-navy" /><span className="text-sm">Show inquiry form on public page</span></label>
          <TextArea label="Inquiry disclaimer (shown above the form)" rows={3} value={data.inquiry_disclaimer} onChange={(v) => setData({ ...data, inquiry_disclaimer: v })} testId="fr-inq-disclaimer" />
        </div>
      )}

      {tab === "halls" && (
        <ListEditor
          title="Halls"
          items={data.halls}
          onChange={(halls) => setData({ ...data, halls })}
          blank={BLANK_HALL}
          uploadImage={uploadImage}
          kind="hall"
          showCapacity
        />
      )}

      {tab === "addons" && (
        <div className="max-w-4xl">
          <div className="rounded-2xl bg-gold/10 border border-gold/30 p-4 mb-6 text-sm text-ink/75">
            Add-ons appear inside the "Optional add-ons" section on the public Facility Rentals page.
            Use the <span className="font-semibold">Capacity</span> column for the unit / inventory count
            (e.g. 800 sets of dinnerware).
          </div>
          <ListEditor
            title="Add-ons"
            items={data.addons}
            onChange={(addons) => setData({ ...data, addons })}
            blank={BLANK_ADDON}
            uploadImage={uploadImage}
            kind="addon"
            showCapacity
            capacityLabel="Inventory"
          />
        </div>
      )}

      {tab === "process" && (
        <div className="max-w-3xl">
          <h3 className="font-serif text-xl mb-3 text-ink">Process steps</h3>
          <div className="space-y-3">
            {(data.process_steps || []).map((s, i) => (
              <div key={i} className="rounded-xl bg-white border border-sand p-4 space-y-2">
                <input value={s.title} onChange={(e) => {
                  const arr = [...data.process_steps]; arr[i] = { ...arr[i], title: e.target.value }; setData({ ...data, process_steps: arr });
                }} placeholder="Step title" className="w-full px-3 py-2 rounded-lg border border-sand focus:outline-none focus:border-navy" />
                <textarea rows={2} value={s.description} onChange={(e) => {
                  const arr = [...data.process_steps]; arr[i] = { ...arr[i], description: e.target.value }; setData({ ...data, process_steps: arr });
                }} placeholder="Description" className="w-full px-3 py-2 rounded-lg border border-sand focus:outline-none focus:border-navy resize-none" />
                <div className="flex justify-end">
                  <button onClick={() => { const arr = data.process_steps.filter((_, j) => j !== i); setData({ ...data, process_steps: arr }); }} className="text-red-600 hover:text-red-800 text-xs inline-flex items-center gap-1"><Trash2 size={12} /> Remove</button>
                </div>
              </div>
            ))}
          </div>
          <button onClick={() => setData({ ...data, process_steps: [...(data.process_steps || []), { ...BLANK_STEP, order: (data.process_steps || []).length }] })}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-gold/20 hover:bg-gold/35 text-navy px-5 py-2.5 text-xs font-semibold uppercase tracking-wider">
            <Plus size={12} /> Add step
          </button>
        </div>
      )}

      {tab === "gallery" && (
        <div className="max-w-4xl">
          <h3 className="font-serif text-xl mb-3 text-ink">Gallery images (shown at the bottom of the page)</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {(data.gallery_images || []).map((url, i) => (
              <div key={i} className="relative group rounded-xl overflow-hidden border border-sand">
                <img src={url} alt="" className="w-full aspect-square object-cover" />
                <button onClick={() => setData({ ...data, gallery_images: data.gallery_images.filter((_, j) => j !== i) })}
                  className="absolute top-2 right-2 w-7 h-7 rounded-md bg-red-500/90 text-white flex items-center justify-center opacity-0 group-hover:opacity-100"><Trash2 size={12} /></button>
              </div>
            ))}
          </div>
          <label className="mt-5 inline-flex items-center gap-2 rounded-full bg-navy text-cream px-5 py-2.5 text-sm font-semibold uppercase tracking-wider cursor-pointer hover:bg-navy-900">
            <Upload size={12} /> Upload photos (multi)
            <input type="file" accept="image/*" multiple className="hidden" onChange={async (e) => {
              const files = Array.from(e.target.files || []);
              const urls = [];
              const errors = [];
              for (const f of files) {
                try { urls.push(await uploadImage(f)); }
                catch (err) { errors.push(err?.message || "upload failed"); }
              }
              if (urls.length) setData({ ...data, gallery_images: [...(data.gallery_images || []), ...urls] });
              e.target.value = "";
              if (urls.length) toast.success(`${urls.length} added — click Save to persist`);
              if (errors.length) toast.error(`${errors.length} failed — ${errors[0]}`);
            }} />
          </label>
        </div>
      )}

      {tab === "inquiries" && (
        <div>
          {inquiries.length === 0 ? <div className="text-ink/50 italic py-12 text-center">No inquiries yet.</div> : (
            <div className="space-y-3">
              {inquiries.map((iq) => (
                <div key={iq.id} data-testid={`inq-row-${iq.id}`} className="rounded-2xl bg-white border border-sand p-5">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="font-medium text-ink text-lg">{iq.name} <span className="text-ink/50 text-sm">· {iq.email} · {iq.phone || "no phone"}</span></div>
                      <div className="mt-1 text-xs text-ink/60 flex gap-3 flex-wrap">
                        {(iq.halls || (iq.hall ? [iq.hall] : [])).map((h) => (
                          <span key={h} className="bg-gold/20 text-navy px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider">{h}</span>
                        ))}
                        {iq.event_date && <span>📅 {iq.event_date}</span>}
                        {iq.event_type && <span>· {iq.event_type}</span>}
                        {iq.expected_guests && <span>· {iq.expected_guests} guests</span>}
                        <span className="text-ink/40">{new Date(iq.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <a href={`mailto:${iq.email}`} className="inline-flex items-center gap-1 text-emerald_deep text-xs px-3 py-1.5 rounded-full border border-sand hover:bg-sand/40"><Mail size={12} /> Reply</a>
                      <button onClick={() => deleteInquiry(iq.id)} className="inline-flex items-center gap-1 text-red-600 text-xs px-3 py-1.5 rounded-full border border-sand hover:bg-red-50"><Trash2 size={12} /></button>
                    </div>
                  </div>
                  <div className="mt-4 text-sm text-ink/80 whitespace-pre-wrap">{iq.message}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- list editor for halls/addons ----------
function ListEditor({ title, items, onChange, blank, uploadImage, kind, capacityLabel = "Capacity" }) {
  const safeItems = items || [];
  const update = (i, patch) => onChange(safeItems.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const remove = (i) => onChange(safeItems.filter((_, idx) => idx !== i));
  const move = (i, d) => {
    const j = i + d; if (j < 0 || j >= safeItems.length) return;
    const arr = [...safeItems]; [arr[i], arr[j]] = [arr[j], arr[i]];
    arr.forEach((it, k) => it.order = k);
    onChange(arr);
  };

  return (
    <div className="max-w-4xl">
      <h3 className="font-serif text-xl mb-3 text-ink">{title}</h3>
      <div className="space-y-4">
        {safeItems.map((it, i) => (
          <div key={i} data-testid={`${kind}-${i}`} className="rounded-2xl bg-white border border-sand p-5 space-y-3">
            <div className="grid md:grid-cols-[1fr,120px,1fr,auto] gap-3 items-start">
              <input value={it.name || ""} onChange={(e) => update(i, { name: e.target.value })} placeholder="Name" className="px-3 py-2 rounded-lg border border-sand" />
              <input type="number" value={it.capacity || ""} onChange={(e) => update(i, { capacity: Number(e.target.value) })} placeholder={capacityLabel} className="px-3 py-2 rounded-lg border border-sand" />
              <input value={it.price || ""} onChange={(e) => update(i, { price: e.target.value })} placeholder="Price (e.g. $4,000)" className="px-3 py-2 rounded-lg border border-sand" />
              <div className="flex gap-1">
                <button onClick={() => move(i, -1)} className="p-2 text-ink/50 hover:text-navy"><ArrowUp size={12} /></button>
                <button onClick={() => move(i, 1)} className="p-2 text-ink/50 hover:text-navy"><ArrowDown size={12} /></button>
                <button onClick={() => remove(i)} className="p-2 text-red-500 hover:bg-red-50 rounded-md"><Trash2 size={12} /></button>
              </div>
            </div>
            <TextArea label="Features (one per line)" rows={3} value={(it.features || []).join("\n")} onChange={(v) => update(i, { features: v.split("\n").map((s) => s.trim()).filter(Boolean) })} />
            <ImageField label="Image" value={it.image_url} onChange={(v) => update(i, { image_url: v })} upload={uploadImage} />
          </div>
        ))}
      </div>
      <button onClick={() => onChange([...safeItems, { ...blank, order: safeItems.length }])}
        data-testid={`${kind}-add-btn`}
        className="mt-4 inline-flex items-center gap-2 rounded-full bg-gold/20 hover:bg-gold/35 text-navy px-5 py-2.5 text-xs font-semibold uppercase tracking-wider">
        <Plus size={12} /> Add {kind}
      </button>
    </div>
  );
}

// ---------- field primitives ----------
function Text({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">{label}</label>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="mt-2 w-full px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-navy" />
    </div>
  );
}
function TextArea({ label, rows = 4, value, onChange, testId }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">{label}</label>
      <textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="mt-2 w-full px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-navy resize-none font-mono text-sm" />
    </div>
  );
}
function ImageField({ label, value, onChange, upload, testId }) {
  const [busy, setBusy] = useState(false);
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">{label}</label>
      <div className="mt-2 flex gap-3 items-start">
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Upload or paste URL" data-testid={testId} className="flex-1 px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-navy" />
        <label className={`inline-flex items-center gap-2 rounded-xl bg-navy text-cream px-4 py-3 text-xs font-semibold cursor-pointer hover:bg-navy-900 ${busy ? "opacity-60 pointer-events-none" : ""}`}>
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Upload
          <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
            const f = e.target.files?.[0]; if (!f) return;
            setBusy(true);
            try {
              const url = await upload(f);
              onChange(url);
              toast.success("Uploaded — click Save to persist");
            } catch (err) {
              // The uploadImage helper now raises with a useful message.
              toast.error(err?.message || "Upload failed");
            }
            finally { setBusy(false); e.target.value = ""; }
          }} />
        </label>
      </div>
      {value && <img src={value} alt="preview" className="mt-3 w-full max-w-xs max-h-40 object-cover rounded-xl border border-sand" />}
    </div>
  );
}
